import { Directive, ElementRef, HostListener, input, OnInit, Renderer2 } from '@angular/core';

@Directive({
  selector: '[sbInputNumber]',
  standalone: true,
})
export class InputNumberDirective implements OnInit {
  min = input<number>(Number.NEGATIVE_INFINITY);
  max = input<number>(Number.POSITIVE_INFINITY);
  enforceRange = input<boolean>(false);
  decimals = input<number>(0);

  private previousValue: number | null = null;

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    const inputEl = this.el.nativeElement;
    this.previousValue = Number(inputEl.value);

    const decimalPlaces = this.decimals();
    const formatted = this.formatValue(inputEl.value, decimalPlaces);

    if (formatted !== inputEl.value) {
      this.renderer.setProperty(inputEl, 'value', formatted);
    }
  }

  /**
   * Prevent keys like ".", ",", "e" when decimals are not allowed
   */
  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    const disallowedKeys = ['e', 'E', '+'];

    if (this.decimals() === 0) {
      disallowedKeys.push('.', ',');
    }

    if (disallowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * In Firefox, clicking the **increment (↑) or decrement (↓) arrows on a number input does not trigger the blur event.
   * This results in the form control not detecting the change properly.
   * This method forces Angular’s change detection to pick up changes that might otherwise be missed.
   */
  @HostListener('click')
  public onClick(): void {
    const inputElement = this.el.nativeElement;
    const currentValue = Number(inputElement.value);

    if (this.previousValue !== null && this.previousValue !== currentValue) {
      this.previousValue = currentValue;
      inputElement.focus();
      inputElement.blur();
    }
  }

  @HostListener('input')
  public onInput(): void {
    if (this.enforceRange()) {
      this.truncateDecimalsDuringInput();
    }
  }

  @HostListener('blur')
  public onBlur(): void {
    const inputEl = this.el.nativeElement;
    this.previousValue = Number(inputEl.value);

    const formatted = this.formatValue(inputEl.value, this.decimals());
    if (formatted !== inputEl.value) {
      this.renderer.setProperty(inputEl, 'value', formatted);
      inputEl.dispatchEvent(new Event('input'));
    }

    if (this.enforceRange()) {
      this.validateAndCorrect();
    }
  }

  private truncateDecimalsDuringInput(): void {
    const inputEl = this.el.nativeElement;
    const rawValue = inputEl.value.trim();
    const decimalPlaces = this.decimals();

    if (!/^[-]?\d*([.,]?\d*)?$/.test(rawValue)) return;

    const isIncomplete = rawValue.endsWith('.') || rawValue.endsWith(',') || rawValue === '-' || rawValue === '';

    if (isIncomplete) return;

    const decimalSeparator = rawValue.includes(',') ? ',' : '.';
    let newValue = rawValue;

    // ✅ Truncate decimal digits if too many
    if (decimalPlaces > 0 && rawValue.includes(decimalSeparator)) {
      const [intPart, decPart] = rawValue.split(decimalSeparator);
      if (decPart.length > decimalPlaces) {
        const truncated = decPart.slice(0, decimalPlaces);
        newValue = `${intPart}${decimalSeparator}${truncated}`;
      }
    }

    // ✅ Clamp to min/max only if number is complete and valid
    const parsed = Number(newValue.replace(',', '.'));
    if (this.enforceRange() && !isNaN(parsed)) {
      let clamped = parsed;
      if (clamped < this.min()) clamped = this.min();
      if (clamped > this.max()) clamped = this.max();

      newValue = clamped.toString();
    }

    if (newValue !== inputEl.value) {
      requestAnimationFrame(() => {
        this.renderer.setProperty(inputEl, 'value', newValue);
      });
    }
  }

  private validateAndCorrect(): void {
    const inputEl = this.el.nativeElement;
    let rawValue = inputEl.value.trim();
    const decimalPlaces = this.decimals();

    if (!/^[-]?\d*([.,]\d*)?$/.test(rawValue)) {
      return; // Skip invalid or incomplete input
    }

    const decimalSeparator = rawValue.includes(',') ? ',' : '.';

    if (decimalPlaces === 0) {
      rawValue = rawValue.split(/[.,]/)[0];
    } else if (rawValue.includes(decimalSeparator)) {
      const [intPart, decPart] = rawValue.split(decimalSeparator);
      const truncatedDec = decPart.slice(0, decimalPlaces);
      rawValue = `${intPart}${decimalSeparator}${truncatedDec}`;
    }

    let numericValue = Number(rawValue.replace(',', '.'));

    // Apply min/max enforcement
    if (numericValue < this.min()) {
      numericValue = this.min();
    } else if (numericValue > this.max()) {
      numericValue = this.max();
    }

    const finalFormatted = numericValue.toFixed(decimalPlaces);

    if (inputEl.value !== finalFormatted) {
      this.renderer.setProperty(inputEl, 'value', finalFormatted);
      inputEl.dispatchEvent(new Event('input'));
    }
  }

  private formatValue(raw: string, decimalPlaces: number): string {
    if (!/^-?\d*\.?\d*$/.test(raw) || raw.endsWith('.') || raw === '-' || raw === '') {
      return raw;
    }

    const num = Number(raw);
    if (isNaN(num)) {
      return raw;
    }

    return num.toFixed(decimalPlaces);
  }
}
